// Track Class Assignment - Interface for assigning classes to track months
import React, { useState, useEffect } from 'react';
import { Plus, X, BookOpen, Calendar, Users, AlertCircle, Save, Trash2 } from 'lucide-react';
import classesService from '../../services/classesService';
import blocksService from '../../services/blocksService';

const LearningBlockClassAssignment = ({ blockId, track }) => {
  const [availableClasses, setAvailableClasses] = useState([]);
  const [months, setMonths] = useState([]); // Array of { monthNumber, monthYear (YYYY-MM), displayName, classes: [] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [selectedMonthForClass, setSelectedMonthForClass] = useState(null);
  const [newMonthYear, setNewMonthYear] = useState('');

  useEffect(() => {
    fetchData();
  }, [blockId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch all available classes
      const classesData = await classesService.getClasses();
      setAvailableClasses(classesData.classes || []);

      // Fetch track details with assigned classes
      const blockData = await blocksService.getBlock(blockId);
      const assignedClasses = blockData.classes || [];

      // Reconstruct months from assigned classes
      if (assignedClasses.length > 0) {
        const monthsMap = new Map();

        assignedClasses.forEach(assignedClass => {
          const monthNum = assignedClass.monthNumber;

          if (!monthsMap.has(monthNum)) {
            // Find the class data to get the actual month/year
            const classData = (classesData.classes || []).find(c => c.id === assignedClass.id);
            const sessionDate = classData?.firstSessionDate || classData?.startDate;

            if (sessionDate) {
              const date = new Date(sessionDate);
              const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
              const displayName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

              monthsMap.set(monthNum, {
                monthNumber: monthNum,
                monthYear: monthYear,
                displayName: displayName,
                classes: []
              });
            }
          }
        });

        // Add classes to their respective months
        assignedClasses.forEach(assignedClass => {
          const month = monthsMap.get(assignedClass.monthNumber);
          if (month) {
            const classData = (classesData.classes || []).find(c => c.id === assignedClass.id);
            if (classData) {
              month.classes.push({
                ...classData,
                sortOrder: assignedClass.sortOrder || 0
              });
            }
          }
        });

        // Convert to array and sort by month number
        const monthsArray = Array.from(monthsMap.values()).sort((a, b) => a.monthNumber - b.monthNumber);
        setMonths(monthsArray);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load classes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format time from 24-hour to 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Get prerequisite class names from IDs
  const getPrerequisiteNames = (prerequisiteIds) => {
    if (!prerequisiteIds || prerequisiteIds.length === 0) return [];
    return prerequisiteIds
      .map(id => {
        const prereqClass = availableClasses.find(c => c.id === id);
        return prereqClass ? prereqClass.title : null;
      })
      .filter(name => name !== null);
  };

  const handleAddMonth = () => {
    if (!newMonthYear) {
      setError('Please select a month and year');
      return;
    }

    // Check if this month already exists
    const exists = months.some(m => m.monthYear === newMonthYear);
    if (exists) {
      setError('This month has already been added');
      return;
    }

    const [year, month] = newMonthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    const displayName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const newMonth = {
      monthNumber: months.length + 1,
      monthYear: newMonthYear,
      displayName: displayName,
      classes: []
    };

    setMonths([...months, newMonth]);
    setShowMonthModal(false);
    setNewMonthYear('');
    setError('');
  };

  const handleDeleteMonth = (monthNumber) => {
    setMonths(months.filter(m => m.monthNumber !== monthNumber));
  };

  const getClassesForMonth = (monthYear) => {
    // Filter classes where firstSessionDate is in the selected month
    const [year, month] = monthYear.split('-');

    return availableClasses.filter(cls => {
      const sessionDate = cls.firstSessionDate || cls.startDate;
      if (!sessionDate) return false;

      const date = new Date(sessionDate);
      const classYear = date.getFullYear();
      const classMonth = date.getMonth() + 1;

      return classYear === parseInt(year) && classMonth === parseInt(month);
    });
  };

  const handleAddClassToMonth = (classId, monthNumber) => {
    const month = months.find(m => m.monthNumber === monthNumber);
    if (!month) return;

    const classToAdd = availableClasses.find(c => c.id === classId);
    if (!classToAdd) return;

    // Check if already assigned to this month
    const alreadyInMonth = month.classes.some(c => c.id === classId);
    if (alreadyInMonth) {
      setError('This class is already assigned to this month');
      return;
    }

    // Add class to month
    const updatedMonths = months.map(m => {
      if (m.monthNumber === monthNumber) {
        return {
          ...m,
          classes: [...m.classes, {
            ...classToAdd,
            sortOrder: m.classes.length
          }]
        };
      }
      return m;
    });

    setMonths(updatedMonths);
    setShowAddClassModal(false);
    setSelectedMonthForClass(null);
    setError('');
  };

  const handleRemoveClassFromMonth = (classId, monthNumber) => {
    const updatedMonths = months.map(m => {
      if (m.monthNumber === monthNumber) {
        return {
          ...m,
          classes: m.classes.filter(c => c.id !== classId)
        };
      }
      return m;
    });

    setMonths(updatedMonths);
  };

  const handleSaveAssignments = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Build array of all class assignments with their month numbers
      const classAssignments = [];
      months.forEach(month => {
        month.classes.forEach((cls, index) => {
          classAssignments.push({
            classId: cls.id,
            monthNumber: month.monthNumber,
            sortOrder: index
          });
        });
      });

      await blocksService.assignClasses(blockId, classAssignments);

      // Calculate total base price and materials fees
      let totalBasePrice = 0;
      let totalMaterialsFees = 0;

      classAssignments.forEach(assignment => {
        const classData = availableClasses.find(c => c.id === assignment.classId);
        if (classData) {
          totalBasePrice += parseFloat(classData.price || 0);
          totalMaterialsFees += parseFloat(classData.materialsFee || 0);
        }
      });

      const numberOfMonths = months.length;

      // Calculate monthly payment
      const monthlyPayment = numberOfMonths > 1
        ? Math.round(totalBasePrice / numberOfMonths)
        : totalBasePrice;

      // Update the track with the calculated prices
      await blocksService.updateBlock(blockId, {
        priceMonthly: monthlyPayment,
        totalClassFees: totalMaterialsFees,
        priceUpfront: totalBasePrice
      });

      setSuccess('Class assignments saved successfully! Pricing updated.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving assignments:', err);
      setError('Failed to save assignments. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Generate month/year options for next 24 months
  const generateMonthOptions = () => {
    const options = [];
    const today = new Date();

    for (let i = 0; i < 24; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }

    return options;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  const monthOptions = generateMonthOptions();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Assign Classes to Block: {track?.name || 'Loading...'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Build your block by adding months and assigning classes to each month
          </p>
        </div>
        <button
          onClick={handleSaveAssignments}
          disabled={saving || months.length === 0}
          className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 disabled:bg-pink-300 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Assignments'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">
          <p>{success}</p>
        </div>
      )}

      {/* Add Month Button */}
      <div>
        <button
          onClick={() => setShowMonthModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#7A99D8] hover:bg-[#6A89C8] text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Month {months.length + 1}
        </button>
      </div>

      {/* Months List */}
      {months.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No months added yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Click "Add Month 1" to start building your block
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {months.map((month) => (
            <div key={month.monthNumber} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              {/* Month Header */}
              <div className="bg-pink-50 dark:bg-pink-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                  Month {month.monthNumber}: {month.displayName}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedMonthForClass(month);
                      setShowAddClassModal(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add Class
                  </button>
                  <button
                    onClick={() => handleDeleteMonth(month.monthNumber)}
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title="Delete this month"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Classes in this month */}
              <div className="p-4">
                {month.classes.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No classes assigned to this month yet. Click "Add Class" to get started.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {month.classes.map((cls) => (
                      <div
                        key={cls.id}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-500 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">
                              {cls.title}
                            </h4>

                            {/* Class Details */}
                            <div className="space-y-1.5">
                              {/* Day and Time */}
                              {(cls.dayOfWeek || cls.startTime) && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                  <Calendar className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                  <span>
                                    {cls.dayOfWeek && `${cls.dayOfWeek}s`}
                                    {cls.dayOfWeek && cls.startTime && ' at '}
                                    {formatTime(cls.startTime)}
                                    {cls.duration && ` (${cls.duration} min)`}
                                  </span>
                                </div>
                              )}

                              {/* Session Dates */}
                              {(cls.firstSessionDate || cls.lastSessionDate) && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                  <Calendar className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                  <span>
                                    {cls.firstSessionDate && (() => {
                                      const [year, month, day] = cls.firstSessionDate.split('T')[0].split('-');
                                      const date = new Date(year, month - 1, day, 12, 0, 0);
                                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                    })()}
                                    {cls.firstSessionDate && cls.lastSessionDate && ' - '}
                                    {cls.lastSessionDate && (() => {
                                      const [year, month, day] = cls.lastSessionDate.split('T')[0].split('-');
                                      const date = new Date(year, month - 1, day, 12, 0, 0);
                                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                    })()}
                                  </span>
                                </div>
                              )}

                              {/* Price */}
                              {(cls.price || cls.materialsFee) && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                  <span className="font-semibold text-green-700 dark:text-green-400">
                                    ${parseFloat(cls.price || 0).toFixed(2)}
                                    {cls.materialsFee && parseFloat(cls.materialsFee) > 0 && (
                                      <span className="text-gray-500 dark:text-gray-400 ml-1">
                                        (+ ${parseFloat(cls.materialsFee).toFixed(2)} materials)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Category and Skill Level badges */}
                              <div className="flex flex-wrap gap-1.5">
                                {cls.skillLevel && (
                                  <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded font-medium">
                                    {cls.skillLevel}
                                  </span>
                                )}
                                {cls.category && (
                                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">
                                    {cls.category}
                                  </span>
                                )}
                              </div>

                              {/* Prerequisites */}
                              {cls.prerequisiteClassIds && cls.prerequisiteClassIds.length > 0 && (() => {
                                const prereqNames = getPrerequisiteNames(cls.prerequisiteClassIds);
                                return prereqNames.length > 0 && (
                                  <div className="flex items-start gap-1.5">
                                    <AlertCircle className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                                    <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                      {prereqNames.length === 1 ? (
                                        <span>Prerequisite: {prereqNames[0]}</span>
                                      ) : (
                                        <div>
                                          <div>Prerequisites:</div>
                                          <ul className="list-disc list-inside ml-1 mt-0.5">
                                            {prereqNames.map((name, idx) => (
                                              <li key={idx}>{name}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveClassFromMonth(cls.id, month.monthNumber)}
                            className="ml-3 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded transition-colors flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Month Modal */}
      {showMonthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Add Month {months.length + 1}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select the month and year for Month {months.length + 1}
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Month/Year
              </label>
              <select
                value={newMonthYear}
                onChange={(e) => setNewMonthYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select month...</option>
                {monthOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMonthModal(false);
                  setNewMonthYear('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMonth}
                className="flex-1 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-medium transition-colors"
              >
                Add Month
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Class Modal */}
      {showAddClassModal && selectedMonthForClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Add Class to {selectedMonthForClass.displayName}
              </h3>
              <button
                onClick={() => {
                  setShowAddClassModal(false);
                  setSelectedMonthForClass(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {(() => {
                const filteredClasses = getClassesForMonth(selectedMonthForClass.monthYear);

                if (filteredClasses.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <BookOpen className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400">
                        No classes available with first session in {selectedMonthForClass.displayName}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-2">
                    {filteredClasses.map((cls) => {
                      const alreadyAssigned = selectedMonthForClass.classes.some(c => c.id === cls.id);

                      return (
                        <button
                          key={cls.id}
                          onClick={() => !alreadyAssigned && handleAddClassToMonth(cls.id, selectedMonthForClass.monthNumber)}
                          disabled={alreadyAssigned}
                          className={`w-full text-left p-4 border rounded-lg transition-colors ${
                            alreadyAssigned
                              ? 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-not-allowed opacity-50'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/10'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">{cls.title}</h4>

                              {/* Class Details */}
                              <div className="space-y-1.5">
                                {/* Day and Time */}
                                {(cls.dayOfWeek || cls.startTime) && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                    <Calendar className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                    <span>
                                      {cls.dayOfWeek && `${cls.dayOfWeek}s`}
                                      {cls.dayOfWeek && cls.startTime && ' at '}
                                      {formatTime(cls.startTime)}
                                      {cls.duration && ` (${cls.duration} min)`}
                                    </span>
                                  </div>
                                )}

                                {/* Session Dates */}
                                {(cls.firstSessionDate || cls.lastSessionDate) && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                    <Calendar className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                    <span>
                                      {cls.firstSessionDate && (() => {
                                        const [year, month, day] = cls.firstSessionDate.split('T')[0].split('-');
                                        const date = new Date(year, month - 1, day, 12, 0, 0);
                                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                      })()}
                                      {cls.firstSessionDate && cls.lastSessionDate && ' - '}
                                      {cls.lastSessionDate && (() => {
                                        const [year, month, day] = cls.lastSessionDate.split('T')[0].split('-');
                                        const date = new Date(year, month - 1, day, 12, 0, 0);
                                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                      })()}
                                    </span>
                                  </div>
                                )}

                                {/* Price */}
                                {(cls.price || cls.materialsFee) && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-semibold text-green-700 dark:text-green-400">
                                      ${parseFloat(cls.price || 0).toFixed(2)}
                                      {cls.materialsFee && parseFloat(cls.materialsFee) > 0 && (
                                        <span className="text-gray-500 dark:text-gray-400 ml-1">
                                          (+ ${parseFloat(cls.materialsFee).toFixed(2)} materials)
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}

                                {/* Spots Left */}
                                {cls.maxCapacity && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                    <Users className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                    <span>
                                      {(() => {
                                        const spotsLeft = (cls.maxCapacity || 0) - (cls.currentEnrollment || 0);
                                        const isLow = spotsLeft <= 3 && spotsLeft > 0;
                                        const isFull = spotsLeft <= 0;
                                        return (
                                          <span className={isFull ? 'text-red-600 dark:text-red-400 font-semibold' : isLow ? 'text-orange-600 dark:text-orange-400 font-semibold' : ''}>
                                            {isFull ? 'Full' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                                          </span>
                                        );
                                      })()}
                                    </span>
                                  </div>
                                )}

                                {/* Category and Skill Level badges */}
                                <div className="flex flex-wrap gap-1.5">
                                  {cls.skillLevel && (
                                    <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded font-medium">
                                      {cls.skillLevel}
                                    </span>
                                  )}
                                  {cls.category && (
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">
                                      {cls.category}
                                    </span>
                                  )}
                                </div>

                                {/* Prerequisites */}
                                {cls.prerequisiteClassIds && cls.prerequisiteClassIds.length > 0 && (() => {
                                  const prereqNames = getPrerequisiteNames(cls.prerequisiteClassIds);
                                  return prereqNames.length > 0 && (
                                    <div className="flex items-start gap-1.5">
                                      <AlertCircle className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                                      <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                        {prereqNames.length === 1 ? (
                                          <span>Prerequisite: {prereqNames[0]}</span>
                                        ) : (
                                          <div>
                                            <div>Prerequisites:</div>
                                            <ul className="list-disc list-inside ml-1 mt-0.5">
                                              {prereqNames.map((name, idx) => (
                                                <li key={idx}>{name}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                            {alreadyAssigned && (
                              <span className="ml-4 text-xs text-gray-500 dark:text-gray-400 font-medium">Assigned</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningBlockClassAssignment;
